import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, MoreVertical, CheckCircle, XCircle, Package } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, Table, Pagination, Button, Badge,
    SearchInput, Select, Dropdown, DropdownItem, DropdownDivider,
    ConfirmDialog, toast
} from '../../components/common';
import { goodsReceiptApi } from '../../services/api';
import './GoodsReceipts.css';

export default function GoodsReceiptList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [actionGR, setActionGR] = useState(null);
    const [actionType, setActionType] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['goodsReceipts', page, pageSize, search, statusFilter],
        queryFn: () => goodsReceiptApi.list({ page, limit: pageSize, search, status: statusFilter }),
        select: (res) => res.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ id, action }) => {
            switch (action) {
                case 'accept': return goodsReceiptApi.accept(id);
                case 'reject': return goodsReceiptApi.reject(id, { reason: 'Quality issue' });
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            toast.success(`Goods ${action}ed successfully`);
            queryClient.invalidateQueries(['goodsReceipts']);
            setActionGR(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const config = {
            'pending_inspection': 'warning',
            'inspected': 'info',
            'accepted': 'success',
            'rejected': 'danger',
            'partial': 'warning'
        };
        return <Badge variant={config[status] || 'default'}>{status?.replace('_', ' ')}</Badge>;
    };

    const columns = [
        {
            key: 'grNumber',
            title: 'GR Number',
            dataIndex: 'grNumber',
            render: (num) => <span className="gr-number">{num}</span>
        },
        {
            key: 'poNumber',
            title: 'PO Number',
            dataIndex: 'purchaseOrder',
            render: (po) => <span className="po-ref">{po?.poNumber}</span>
        },
        {
            key: 'vendor',
            title: 'Vendor',
            render: (_, row) => <span>{row.purchaseOrder?.vendor?.name || '-'}</span>
        },
        {
            key: 'receivedDate',
            title: 'Received Date',
            dataIndex: 'receivedDate',
            render: (date) => formatDate(date)
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); navigate(`/goods-receipts/${row.id}`); }}>
                                View Details
                            </DropdownItem>
                            {row.status === 'inspected' && (
                                <>
                                    <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionGR(row); setActionType('accept'); }}>
                                        Accept
                                    </DropdownItem>
                                    <DropdownItem icon={XCircle} onClick={() => { close(); setActionGR(row); setActionType('reject'); }}>
                                        Reject
                                    </DropdownItem>
                                </>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending_inspection', label: 'Pending Inspection' },
        { value: 'inspected', label: 'Inspected' },
        { value: 'accepted', label: 'Accepted' },
        { value: 'rejected', label: 'Rejected' }
    ];

    return (
        <div className="gr-page">
            <PageHeader
                title="Goods Receipts"
                subtitle="Manage goods receipts"
                breadcrumbs={[{ label: 'Goods Receipts', path: '/goods-receipts' }]}
                actions={
                    <Button icon={Plus} onClick={() => navigate('/goods-receipts/create')}>
                        Create GR
                    </Button>
                }
            />

            <Card>
                <div className="gr-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search goods receipts..."
                        className="gr-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        className="gr-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No goods receipts found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="gr-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={!!actionGR}
                onClose={() => { setActionGR(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionGR?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Goods`}
                message={`Are you sure you want to ${actionType} this goods receipt?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                variant={actionType === 'reject' ? 'danger' : 'primary'}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
